import { useState, useEffect } from 'react';
import { routeNames } from '../../configs/routePaths';
import { cnpjMask } from '@/utils/masks';
import Sensedia from '../../services/sensedia';
import { CircularLoading } from '../../components/circular-loading';
import { useUserContext } from '../../contexts/user.context';
import { Link } from "react-router-dom"

const PassoModal = {
  SESSAO_EXPIRADA: 0,
  ERRO_AUTENTICACAO: 1
};

const VrAccessLogin = ({ isVrAccessDeepLink = true }) => {
  const sensedia = new Sensedia();
  const [textoTituloModal, setTextoTituloModal] = useState<JSX.Element | null>(null);
  const [passoModal, setPassoModal] = useState<number>(PassoModal.SESSAO_EXPIRADA);
  const [isLoadingGeneral, setIsLoadingGeneral] = useState(false);
  const { vrAccessAuth } = useUserContext();

  const params = new URLSearchParams(location.search);
  const destination = params.get('destination') ?? '';
  const cnpj = params.get('cnpj');
  const accessToken = params.get('accessToken');

  const vrAccessRequest = {
    cnpj: cnpjMask(cnpj),
    vrAccessToken: accessToken,
    isVrAccessDeepLink: isVrAccessDeepLink
  };

  const getRouteDestination = (destination: string, isVrAccessDeepLink: boolean) => {
    if (isVrAccessDeepLink) {
      const destinationMap: Record<string, string> = {
        cadastro: `/${routeNames.cadastros}`,
        pedidos: `/${routeNames.pedidos}`,
        importacao: routeNames.importacaoOld,
        logistica: `/${routeNames.encomendas}`,
        'regra-das-operadoras': `/${routeNames.regrasDasOperadoras}`,
        solicitacoes: routeNames.solicitacoes,
        'melhor-rota': `/${routeNames.melhorRota}`,
        relatorio: `/${routeNames.relatoriosOld}`
      };
      
      return destinationMap[destination];
    }

    return `${routeNames.dashboard}`;
  };

  const getModalContent = (step: number): JSX.Element => {
    switch (step) {
      case PassoModal.SESSAO_EXPIRADA:
        return (
          <>
            <p className="font-bold text-lg">Sessão expirada</p>
            <p className="text-sm">
              Para continuar suas atividades, entre novamente com seu login e senha
            </p>
          </>
        );
      case PassoModal.ERRO_AUTENTICACAO:
        return (
          <>
            <p className="font-bold text-lg">Acesso em processamento</p>
            <p className="text-sm mt-3">
              Se você aceitou o contrato <b>nas últimas 48 horas</b>, seu acesso a solução <b>Mobilidade</b> está sendo ativado. 
            </p>
            <p className="text-sm mt-3">
              Caso o prazo já tenha sido concluído e o acesso ainda não esteja disponível, tente novamente mais tarde.
            </p>
          </>
        );
      default:
        return null;
    }
  };

  const validateDestination = (isVrAccessDeepLink: boolean) => {
    if (isVrAccessDeepLink){
      const validDestinations = [
        'cadastro', 'pedidos', 'importacao', 'logistica',
        'regra-das-operadoras', 'solicitacoes', 'melhor-rota', 'relatorio'
      ];
      return destination && validDestinations.includes(destination);
    }
    return true;
  }

  useEffect(() => {
    const fetchData = async () => {
      const isDestinationValid = validateDestination(isVrAccessDeepLink );
        
      const isCnpjValid = cnpj && /^[A-Z0-9]{12}[0-9]{2}$/i.test(cnpj.replace(/[^a-zA-Z0-9]/g, ""));

      if (!isDestinationValid || !isCnpjValid) {
        console.warn('Parâmetros inválidos:', { destination, cnpj });
        setPassoModal(PassoModal.ERRO_AUTENTICACAO);
        return;
      }

      setIsLoadingGeneral(true);

      try {

        let targetRoute = getRouteDestination(destination, isVrAccessDeepLink);

        if (!targetRoute) {
          setPassoModal(PassoModal.ERRO_AUTENTICACAO);
          return;
        }
        await sensedia.getGrandCodeAndSetTokenStorage();
        await vrAccessAuth(vrAccessRequest, targetRoute);

      } catch (error: any) {
        const errorInvalidTokenAccessVr = 'Acesso VR inválido'.toLowerCase();
        const modalStep = error?.status == 401 && error?.message.toLowerCase()?.includes(errorInvalidTokenAccessVr) ? PassoModal.SESSAO_EXPIRADA : PassoModal.ERRO_AUTENTICACAO;
        console.error('Erro:', error?.message);
        setPassoModal(modalStep);
        setIsLoadingGeneral(false);
      }
    };

    fetchData();
  }, [cnpj, accessToken]);

  useEffect(() => {
    setTextoTituloModal(getModalContent(passoModal));
  }, [passoModal]);

  return (
    <main className="h-screen flex flex-col">
      <div className="fixed inset-0 flex items-center justify-center z-50">
        {isLoadingGeneral ? (
          <CircularLoading
            isLoading={true}
            shouldBlur={true}
            hasText
          >
            Carregando...
          </CircularLoading>
        ) : (
        <div className="flex flex-col w-full md:w-9/12 max-w-[650px] mx-auto rounded-tr-3xl rounded-bl-3xl border border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-gray-800 p-5 md:p-10">
          <div className="flex justify-center items-center mb-6">
            <img
              src="/images/boneco_pensativo.svg"
              alt="Logo"
              width={160}
              title="Fale conosco"
            />
          </div>
          <div className="bg-gray-200 p-4 text-[10px] text-center rounded-lg">
            {textoTituloModal}
            <Link to={process.env.VITE_APP_SUPERPORTAL_ADDRESS} className="font-bold text-sm mt-6 cursor-pointer inline-block">
              Voltar para tela de login
            </Link>
          </div>
        </div>
        )}
      </div>
    </main>
  );
};

export default VrAccessLogin;
